import type { LibraryItemCategory } from '../types/library-item-category.type';
import type { LibraryItemMetadata } from '../types/library-item-metadata.type';
import { LIBRARY_IDENTIFIER_TYPES, LIBRARY_METADATA_FIELDS } from './library-metadata-fields.constants';

export function isLibraryItemCategory(value: unknown): value is LibraryItemCategory {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LIBRARY_METADATA_FIELDS, value);
}

export function isLibraryItemMetadata(value: unknown, category: LibraryItemCategory): value is LibraryItemMetadata {
  if (!isRecord(value) || value['category'] !== category) return false;
  const fields = LIBRARY_METADATA_FIELDS[category];
  return Object.entries(value).every(([key, entry]): boolean => {
    if (key === 'category' || entry === undefined) return true;
    if (key === 'custom') return isRecord(entry);
    if (!Object.prototype.hasOwnProperty.call(fields, key)) return false;
    switch (fields[key]) {
      case 'text': return typeof entry === 'string';
      case 'number': return typeof entry === 'number' && Number.isFinite(entry);
      case 'list': return Array.isArray(entry) && entry.every((part: unknown): boolean => typeof part === 'string');
      case 'identifier': return typeof entry === 'string' && LIBRARY_IDENTIFIER_TYPES.includes(entry);
      case 'platform': return isRecord(entry) && typeof entry['value'] === 'string' && typeof entry['label'] === 'string' && typeof entry['isCustom'] === 'boolean';
      default: return false;
    }
  });
}

/** Existing opaque fields may round-trip unchanged; new/changed values use the current contract. */
export function isLibraryItemMetadataUpdate(value: unknown, category: LibraryItemCategory, previous: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isRecord(previous) || previous['category'] !== category) return isLibraryItemMetadata(value, category);
  const changed = Object.fromEntries(Object.entries(value).filter(([key, entry]): boolean =>
    key === 'category' || !Object.prototype.hasOwnProperty.call(previous, key) || JSON.stringify(entry) !== JSON.stringify(previous[key])));
  return isLibraryItemMetadata(changed, category);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
